import React, { useEffect, useMemo, useState } from 'react';
import { AppState, Dimensions, Keyboard, Platform, View } from 'react-native';
import { colors } from '../../utils/theme';
import { useOnLayoutChange } from '../v2BaseComponents/LayoutChangeEventProvider';

export type KeyboardRestrictedViewOwnProps = {
    children: React.ReactNode;
};

export type KeyboardRestrictedViewProps = KeyboardRestrictedViewOwnProps;

export type IsInKeyboardRestrictedViewContext = {
    isKeyboardRestrictedView: boolean;
    windowHeightWithKeyboard: number;
};

const IsKeyboardRestrictedViewContext = React.createContext<IsInKeyboardRestrictedViewContext>({
    isKeyboardRestrictedView: false,
    windowHeightWithKeyboard: Dimensions.get('window').height,
});

const getShouldRestrictViewToKeyboard = () => {
    if (Platform.OS !== 'web') {
        return false;
    }

    return !!window.visualViewport;
};

type ViewportDimensions = {
    width: number;
    height: number;
};

const getWindowDimensions = (): ViewportDimensions => {
    if (!getShouldRestrictViewToKeyboard()) {
        return Dimensions.get('window');
    }

    return {
        width: window.visualViewport?.width || window.innerWidth,
        height: window.visualViewport?.height || window.innerHeight,
    };
};

const scrollToTop = () => {
    /**
     * Scroll to the top of the page
     *
     * Top must be -1 to work on iOS.  0 does not work.  If it is 0, the scroll will not happen if the screen is scrolled
     * down < ~50px....  This is a bug in iOS.
     */
    window.scroll({ top: -1, left: 0, behavior: 'instant' });
};

/**
 * We want to scroll the input component.  The event is called on the nested input, but
 * we want to scroll to the parent component with a label.
 *
 * Doing this dom traversal is a bit hacky for react.  This uses a pretty conservative approach
 * to do a best effort to find that parent component.  Worst case, it will return the input itself.
 * This is not ideal, but ultimately will just cutoff the label above the scrollview
 */
function findInputScrollableParent(element: Element): Element {
    function findFirstParentWithTabIndex(element: Element) {
        let currentElement: Element | null = element;

        while (currentElement) {
            if (currentElement.hasAttribute('tabindex')) {
                return currentElement;
            }
            currentElement = currentElement.parentElement;
        }

        // Return null if no parent with tabindex is found
        return null;
    }

    const maybeParentWithLabel = findFirstParentWithTabIndex(element);
    if (!maybeParentWithLabel) return element;

    // ensure that this is a reasonable parent and not some ancestor far up the dom tree
    const parentWithLabelTop = maybeParentWithLabel.getBoundingClientRect().top;
    const inputTop = element.getBoundingClientRect().top;

    if (parentWithLabelTop < inputTop && inputTop - parentWithLabelTop < 40) {
        return maybeParentWithLabel;
    } else {
        return element;
    }
}

function scrollFocusedElementIntoView() {
    const focusedElement = document.activeElement;
    if (focusedElement) {
        const scrollableParent = findInputScrollableParent(focusedElement);

        scrollableParent.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
        });
    }
}

const NativeKeyboardRestrictedView: React.FC<KeyboardRestrictedViewProps> = (props) => {
    const [isKeyboardVisible, setKeyboardVisible] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const onLayoutChange = useOnLayoutChange(); // allows for absolutely positioned elements to resize

    useEffect(() => {
        // isKeyboardVisible drives the bottom tab bar's visibility and the Android keyboard padding.
        // keyboardDidHide is not always delivered when the keyboard is dismissed during a native screen
        // transition or when the app is backgrounded, which would otherwise leave the bottom navigation
        // hidden (or content padded up) until the app is restarted. Reset the flag on keyboardWillHide
        // and whenever the app leaves the foreground so it can never get stuck in the "open" state.
        const markKeyboardHidden = () => {
            setKeyboardVisible(false);

            onLayoutChange();
        };

        const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', (e) => {
            setKeyboardVisible(true);

            setKeyboardHeight((height) => {
                const flooredHeight = Math.floor(e.endCoordinates.height);
                const isDeltaEnough = Math.abs(flooredHeight - height) > 10;
                if (isDeltaEnough) {
                    return flooredHeight;
                } else {
                    return height;
                }
            });

            onLayoutChange();
        });
        const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', markKeyboardHidden);
        const keyboardWillHideListener = Keyboard.addListener('keyboardWillHide', markKeyboardHidden);
        const appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
            if (nextAppState !== 'active') {
                markKeyboardHidden();
            }
        });

        return () => {
            keyboardDidHideListener.remove();
            keyboardDidShowListener.remove();
            keyboardWillHideListener.remove();
            appStateSubscription.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contextValue = useMemo(() => {
        return {
            isKeyboardRestrictedView: isKeyboardVisible,
            windowHeightWithKeyboard: isKeyboardVisible
                ? Dimensions.get('window').height - keyboardHeight
                : Dimensions.get('window').height,
        };
    }, [isKeyboardVisible, keyboardHeight]);

    return (
        <IsKeyboardRestrictedViewContext.Provider value={contextValue}>
            <View style={{ width: '100%', height: '100%', backgroundColor: colors.Background }}>{props.children}</View>
        </IsKeyboardRestrictedViewContext.Provider>
    );
};

/**
 * A component that limits the view of the app to the area not covered by the keyboard.
 *
 * When not a phone or tablet, the view is not restricted.
 *
 * When on a phone, android devices automatically adjust the view when the keyboard is opened.  However,
 * iOS devices do not adjust the view automatically.  This results in a suboptimal/non-native experience, where
 * the keyboard covers part of the view.
 */
const WebKeyboardRestrictedView: React.FC<KeyboardRestrictedViewProps> = (props) => {
    const shouldRestrictViewToKeyboard = useMemo(() => getShouldRestrictViewToKeyboard(), []);
    const startDimensions = useMemo(() => getWindowDimensions(), []);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [lastResizeDimensions, setLastResizeDimensions] = useState(startDimensions);

    useEffect(() => {
        const handleResize = () => {
            if (!shouldRestrictViewToKeyboard) return;

            const currentDimensions = getWindowDimensions();
            setLastResizeDimensions(currentDimensions);
            const isKeyboardVisible = getWindowDimensions().height < startDimensions.height * 0.9;
            setIsKeyboardVisible(isKeyboardVisible);

            if (isKeyboardVisible) {
                setTimeout(scrollToTop, 50);

                // Programmatic scrolling can mess with the browser default of scrolling the focused element into view.  Wait a bit and do so manually
                setTimeout(scrollFocusedElementIntoView, 100);
            }
        };

        window.visualViewport?.addEventListener('resize', handleResize);

        return () => {
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [shouldRestrictViewToKeyboard, startDimensions.height]);

    useEffect(() => {
        if (!isKeyboardVisible) return;

        /*
            keep "scrolling to the top" of the viewport while the keyboard is up.
            The visual space is constrained to the area not covered by the keyboard, this just prevents the user from scrolling down to empty space
        */
        const scrollToTopInterval = setInterval(scrollToTop, 100);

        return () => {
            clearInterval(scrollToTopInterval);
        };
    }, [isKeyboardVisible]);

    const contextValue = useMemo(() => {
        return {
            isKeyboardRestrictedView: isKeyboardVisible,
            windowHeightWithKeyboard: Dimensions.get('window').height,
        };
    }, [isKeyboardVisible]);

    return (
        <IsKeyboardRestrictedViewContext.Provider value={contextValue}>
            <View style={{ width: '100%', height: '100%', backgroundColor: colors.Background }}>
                <View
                    style={{ width: '100%', height: lastResizeDimensions.height, backgroundColor: colors.Background }}
                >
                    {props.children}
                </View>
                <View
                    style={{
                        width: '100%',
                        height: startDimensions.height - lastResizeDimensions.height,
                        backgroundColor: colors.Background,
                    }}
                />
            </View>
        </IsKeyboardRestrictedViewContext.Provider>
    );
};

export default React.memo(
    Platform.select({
        web: WebKeyboardRestrictedView,
        default: NativeKeyboardRestrictedView,
    }),
);

export const useIsKeyboardRestrictedView = () => {
    return React.useContext(IsKeyboardRestrictedViewContext).isKeyboardRestrictedView;
};

export const useWindowHeightWithKeyboard = () => {
    return React.useContext(IsKeyboardRestrictedViewContext).windowHeightWithKeyboard;
};
